import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "@/components/ui/Container";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { PageHead } from "./components/PageHead";
import { FormInfoBox } from "./components/FormInfoBox";
import { FormFields } from "./components/FormFields";
import { HoneyPotField } from "./components/HoneyPotField";
import { ReCaptchaField } from "./components/ReCaptchaField";
import { formSchema, type FormValues } from "./schema/rustCourseFormSchema";
import { Logo } from "@/components/ui/Logo";

// This is a public site key, so it's safe to keep it in the code
const RECAPTCHA_SITE_KEY = "6Lfd8xorAAAAAA_WrrNIdHi19mk0s4Ypx0qVOWd7";
const BACKGROUND_IMAGE = "/lovable-uploads/1aa9c2a1-da7e-46c3-bbbf-1dd9ff491d71.png";

const RustCourseLaunchForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const navigate = useNavigate();
  const honeyRef = useRef<HTMLInputElement>(null);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      title: "",
      linkedin: "",
      phone: "",
      honey: "",
      recaptcha: "",
    },
  });

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const verifyRecaptcha = async (token: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke("verify-recaptcha", {
        body: { token },
      });
      
      if (error) {
        console.error("reCAPTCHA verification error:", error);
        return false;
      }
      
      return data.success === true;
    } catch (error) {
      console.error("Error calling reCAPTCHA verification function:", error);
      return false;
    }
  };

  const onSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true);
      
      if (values.honey || (honeyRef.current && honeyRef.current.value)) {
        console.log("Bot detected");
        setTimeout(() => {
          navigate("/campaigns/rust-course-launch-thank-you");
        }, 1500);
        return;
      }

      if (!captchaValue) {
        toast.error("Please complete the reCAPTCHA verification");
        setIsSubmitting(false);
        return;
      }

      // Verify reCAPTCHA token server-side
      const isVerified = await verifyRecaptcha(captchaValue);
      
      if (!isVerified) {
        toast.error("reCAPTCHA verification failed. Please try again.");
        setIsSubmitting(false);
        return;
      }

      const { error } = await supabase.from("rust_interest").insert({
        name: values.name,
        email: values.email,
        company: values.company,
        title: values.title,
        linkedin: values.linkedin || null,
        phone: values.phone,
        reason: "Launch campaign free entry to rust course"
      });

      if (error) {
        throw error;
      }

      navigate("/campaigns/rust-course-launch-thank-you");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("There was an error submitting your form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageHead 
        title="Fledgio Launch Offer - Free Rust Training" 
        description="Join our exclusive Rust course launch campaign for a chance to participate in our programming class."
        backgroundImage={BACKGROUND_IMAGE}
      />

      <div 
        className="min-h-screen relative overflow-hidden bg-cover bg-center"
        style={{ 
          backgroundImage: `url('${BACKGROUND_IMAGE}')`, 
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }} 
      >
        <div 
          className="absolute inset-0 bg-black/50 z-0"
          style={{ backdropFilter: 'blur(1px)' }}
        />
        
        <Container className="relative z-10 py-16 md:py-20">
          <div className="absolute top-4 left-4">
            <Logo className="h-12 md:h-16 hover:opacity-80 transition-opacity" />
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="bg-white/90 backdrop-blur-md shadow-xl rounded-lg overflow-hidden p-6 md:p-8 border border-white/30">
              <FormInfoBox />

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormFields form={form} />
                  
                  <HoneyPotField form={form} />

                  <ReCaptchaField 
                    form={form} 
                    siteKey={RECAPTCHA_SITE_KEY} 
                    onCaptchaChange={handleCaptchaChange} 
                  />

                  <div className="pt-4">
                    <Button 
                      type="submit" 
                      className="w-full py-6 text-lg"
                      disabled={isSubmitting || !captchaValue}
                    >
                      {isSubmitting ? "Submitting..." : "Submit Entry"}
                    </Button>
                  </div>
                  
                  <p className="text-xs text-center text-gray-500 mt-4">
                    By submitting this form, you agree to our <a href="/terms" className="underline hover:text-primary">Terms and Conditions</a> and <a href="/privacy" className="underline hover:text-primary">Privacy Policy</a>.
                  </p>
                </form>
              </Form>
            </div>
          </div>
        </Container>
      </div>
    </>
  );
};

export default RustCourseLaunchForm;
